import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import AssetsSchemaIcon from '@atlaskit/icon-lab/core/assets-schema';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientSearchResultType } from '@atlassian/search-client/common/types';
import { getGenericUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { DisplayNameTooltip } from '../../../../../../common/ui/display-name-tooltip';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { getAssetsEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchAssetsObjectType_AtlassianSearchPage$key } from './__generated__/searchAssetsObjectType_AtlassianSearchPage.graphql';

const SchemaMetadata = ({
	schemaName,
	schemaInternalName,
}: {
	schemaName: string;
	schemaInternalName?: string;
}) => {
	const content = (
		<Inline space="space.050" alignBlock="center">
			<AssetsSchemaIcon size="small" label="" />
			<Text color="inherit">{schemaName}</Text>
		</Inline>
	);

	if (schemaInternalName && fg('cdm_ga')) {
		return (
			<DisplayNameTooltip displayName={schemaName} name={schemaInternalName}>
				{content}
			</DisplayNameTooltip>
		);
	}
	return content;
};
export const SearchAssetsObjectType = ({
	node,
	position,
	searchSessionId,
	primaryProduct,
	primarySubProduct,
	selectedProduct,
	appMode,
	mainContentWidth,
	selectedSites,
	cloudId,
}: SearchResultProps<searchAssetsObjectType_AtlassianSearchPage$key>): React.JSX.Element | null => {
	const data = useFragment(
		graphql`
			fragment searchAssetsObjectType_AtlassianSearchPage on SearchResultAssetsObjectType {
				__typename
				id
				title
				description
				type
				lastModifiedDate
				objectSchemaEntity {
					name
					displayName
				}
				objectTypeEntity {
					name
					displayName
					icon {
						url48
					}
					links {
						web
					}
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [, , , contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			// Normalise to the canonical 1P key ('cmdb') so analytics pipelines
			// see a single identifier. The ARI resource-owner may be 'assets'
			// (assets-site deployments) or 'cmdb' (regular cloud).
			product: ProductKeys.Assets,
			...getSiteMetadata(data.id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type, cloudId, selectedSites]);

	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();
	const url = useMemo(() => {
		const webUrl = data.objectTypeEntity?.links?.web;
		if (!webUrl) {
			return null;
		}
		return getGenericUrlWithAnalyticsQueryParams({
			url: webUrl,
			searchSessionId,
		});
	}, [data.objectTypeEntity?.links?.web, searchSessionId]);

	const getLastModified = useMemo(() => {
		if (!data.lastModifiedDate) {
			return null;
		}
		return <LastModified lastModified={data.lastModifiedDate} />;
	}, [data.lastModifiedDate]);

	const schemaDisplayName = data.objectSchemaEntity?.displayName;
	const schemaInternalName = data.objectSchemaEntity?.name ?? undefined;
	const schemaName = fg('cdm_ga') ? schemaDisplayName || '' : schemaInternalName;

	const headerMetadataParts = [
		['type', <SearchResultType type={data.type as SearchClientSearchResultType} />],
		...(schemaName
			? [
					[
						'schema',
						<SchemaMetadata schemaName={schemaName} schemaInternalName={schemaInternalName} />,
					],
				]
			: []),
		['lastModified', getLastModified],
	];

	const typeDisplayName = data.objectTypeEntity?.displayName;
	const typeInternalName = data.objectTypeEntity?.name;

	const resolvedTitle = fg('cdm_ga') ? typeDisplayName || '' : data.title;

	const enrichedTitle = useMemo(() => {
		return enrichExcerpt(resolvedTitle);
	}, [resolvedTitle]);

	const titleWithTooltip = fg('cdm_ga') ? (
		<DisplayNameTooltip displayName={typeDisplayName || ''} name={typeInternalName || ''}>
			<span>{enrichedTitle}</span>
		</DisplayNameTooltip>
	) : undefined;

	const enrichedDescription = useMemo(() => {
		return enrichExcerpt(data.description);
	}, [data.description]);
	const icon = useMemo(() => {
		return getAssetsEntityIcon({
			iconUrl: data.objectTypeEntity?.icon?.url48,
			type: data.type,
			size: 'medium',
		});
	}, [data.objectTypeEntity?.icon?.url48, data.type]);

	if (!url) {
		return null;
	}

	return (
		<SearchResult
			{...data}
			product={ProductKeys.Assets}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			icon={icon}
			headerMetadataParts={headerMetadataParts}
			title={titleWithTooltip || enrichedTitle}
			description={<DescriptionBody description={enrichedDescription} />}
			position={position}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
			selectedSites={selectedSites}
		/>
	);
};

export const isSearchAssetsObjectType = (
	node?: any,
): node is searchAssetsObjectType_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchResultAssetsObjectType';
};
